import React, { useState, useEffect } from 'react';
import { ordersApi, customersApi } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { Loader2, Pencil, AlertTriangle } from 'lucide-react';
import BillToPicker from './BillToPicker';

/**
 * Edit Dispatch / Invoice Dialog
 * Allows super_admin and admin users to fix typos in invoice numbers, correct
 * amounts, update logistics & financial details on an existing dispatch row.
 * Pieces count and dispatch_type are NOT editable here — those have downstream
 * effects on stage quantities and pending-receipts that need a separate flow.
 */
const EditDispatchDialog = ({ open, orderId, dispatch, onClose, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  // Approved-customers list for the Bill-To picker (lazy-loaded once).
  const [approvedCustomers, setApprovedCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [billToOpen, setBillToOpen] = useState(false);
  // Track the original bill-to so we can send an explicit "" to clear it.
  const [initialBillToId, setInitialBillToId] = useState('');

  useEffect(() => {
    if (!open || !dispatch) return;
    setForm({
      memo_number: dispatch.memo_number || '',
      invoice_number: dispatch.invoice_number || '',
      logistics: dispatch.logistics || '',
      tracking_number: dispatch.tracking_number || '',
      invoice_logistics: dispatch.invoice_logistics || '',
      invoice_tracking: dispatch.invoice_tracking || '',
      notes: dispatch.notes || '',
      total_amount: dispatch.total_amount ?? '',
      gold_weight: dispatch.gold_weight ?? '',
      gold_karat: dispatch.gold_karat || '',
      diamond_weight: dispatch.diamond_weight ?? '',
      metal_value: dispatch.metal_value ?? '',
      diamond_others_value: dispatch.diamond_others_value ?? '',
      payment_type: dispatch.payment_type || '',
      metal_credit_days: dispatch.metal_credit_days ?? '',
      diamond_credit_days: dispatch.diamond_credit_days ?? '',
      has_metal_exchange: !!dispatch.has_metal_exchange,
      metal_exchange_weight: dispatch.metal_exchange_weight ?? '',
      metal_exchange_invoice_number: dispatch.metal_exchange_invoice_number || '',
      dispatch_date: (dispatch.invoice_date || dispatch.invoiced_at || '').slice(0, 10),
      metal_due_date: (dispatch.metal_due_date || '').slice(0, 10),
      diamond_due_date: (dispatch.diamond_due_date || '').slice(0, 10),
      bill_to_customer_id: dispatch.bill_to_customer_id || '',
      bill_to_customer_name: dispatch.bill_to_customer_name || '',
    });
    setInitialBillToId(dispatch.bill_to_customer_id || '');
    // Lazy-load approved customers only when an invoice is being edited.
    const isInv = dispatch.dispatch_type === 'invoice' || dispatch.invoiced;
    if (isInv && approvedCustomers.length === 0 && !loadingCustomers) {
      setLoadingCustomers(true);
      customersApi.getApprovedForOrder()
        .then(res => setApprovedCustomers(res.data || []))
        .catch(() => {})
        .finally(() => setLoadingCustomers(false));
    }
  }, [open, dispatch]);

  if (!dispatch) return null;
  const isInvoice = dispatch.dispatch_type === 'invoice' || dispatch.invoiced;
  const hasPayments = (dispatch.payments || []).length > 0;
  const paymentsTotal = (dispatch.payments || []).reduce((s, p) => s + Number(p.amount || 0), 0);

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      // Convert "" → null for optional numeric/string fields so backend ignores them.
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        // bill_to fields are handled below (need to send "" explicitly to clear).
        if (k === 'bill_to_customer_id' || k === 'bill_to_customer_name') return;
        if (v === '' || v === undefined) return;
        if (['total_amount', 'gold_weight', 'diamond_weight', 'metal_value',
             'diamond_others_value', 'metal_exchange_weight'].includes(k)) {
          payload[k] = Number(v);
        } else if (['metal_credit_days', 'diamond_credit_days'].includes(k)) {
          payload[k] = parseInt(v, 10);
        } else {
          payload[k] = v;
        }
      });
      // Bill-To: only include when the user changed it. Empty string clears the override.
      const currentBt = form.bill_to_customer_id || '';
      if (currentBt !== initialBillToId) {
        payload.bill_to_customer_id = currentBt;
        payload.bill_to_customer_name = currentBt ? (form.bill_to_customer_name || '') : '';
      }
      const res = await ordersApi.updateDispatch(orderId, dispatch.id, payload);
      const changed = Object.keys(res?.data?.changes || {}).length;
      if (changed === 0) {
        toast.info('No changes to save');
      } else {
        toast.success(`Updated ${changed} field${changed === 1 ? '' : 's'}`);
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update dispatch');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-dispatch-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-5 w-5 text-violet-600 dark:text-violet-400" />
            Edit {isInvoice ? 'Invoice' : 'Approval Memo'}
          </DialogTitle>
          <DialogDescription>
            Fix typos, update logistics, or correct financial details. Piece counts and dispatch type cannot be changed here.
          </DialogDescription>
        </DialogHeader>

        {hasPayments && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-800 p-3 text-xs text-amber-700 dark:text-amber-300 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">Payments are recorded against this invoice</p>
              <p>Total amount cannot go below the recorded ₹{paymentsTotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}. Gold karat cannot be changed until payments are deleted.</p>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {/* Numbers */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {!isInvoice && (
              <div>
                <Label className="text-xs">Memo number</Label>
                <Input
                  value={form.memo_number || ''}
                  onChange={e => setField('memo_number', e.target.value)}
                  data-testid="edit-dispatch-memo-number"
                />
              </div>
            )}
            <div>
              <Label className="text-xs">Invoice number{isInvoice && ' *'}</Label>
              <Input
                value={form.invoice_number || ''}
                onChange={e => setField('invoice_number', e.target.value)}
                data-testid="edit-dispatch-invoice-number"
              />
            </div>
            <div>
              <Label className="text-xs">{isInvoice ? 'Invoice date' : 'Memo date'}</Label>
              <Input
                type="date"
                value={form.dispatch_date || ''}
                onChange={e => setField('dispatch_date', e.target.value)}
                data-testid="edit-dispatch-date"
              />
            </div>
            {isInvoice && (
              <div>
                <Label className="text-xs">Total amount (₹)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.total_amount}
                  onChange={e => setField('total_amount', e.target.value)}
                  data-testid="edit-dispatch-total-amount"
                />
              </div>
            )}
          </div>

          {/* Logistics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{isInvoice ? 'Invoice logistics' : 'Logistics (sent via)'}</Label>
              <Input
                value={isInvoice ? form.invoice_logistics : form.logistics}
                onChange={e => setField(isInvoice ? 'invoice_logistics' : 'logistics', e.target.value)}
                placeholder="e.g. Blue Dart"
                data-testid="edit-dispatch-logistics"
              />
            </div>
            <div>
              <Label className="text-xs">{isInvoice ? 'Invoice tracking #' : 'Tracking number'}</Label>
              <Input
                value={isInvoice ? form.invoice_tracking : form.tracking_number}
                onChange={e => setField(isInvoice ? 'invoice_tracking' : 'tracking_number', e.target.value)}
                placeholder="Tracking ID"
                data-testid="edit-dispatch-tracking"
              />
            </div>
          </div>

          {/* Notes for memos */}
          {!isInvoice && (
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea
                rows={2}
                value={form.notes || ''}
                onChange={e => setField('notes', e.target.value)}
                data-testid="edit-dispatch-notes"
              />
            </div>
          )}

          {/* Financial — invoice only */}
          {isInvoice && (
            <>
              <div className="pt-2 border-t">
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3">Financial details</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Gold weight (g)</Label>
                    <Input
                      type="number"
                      step="0.001"
                      value={form.gold_weight}
                      onChange={e => setField('gold_weight', e.target.value)}
                      data-testid="edit-dispatch-gold-weight"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Gold karat</Label>
                    <Select
                      value={form.gold_karat || ''}
                      onValueChange={v => setField('gold_karat', v)}
                      disabled={hasPayments}
                    >
                      <SelectTrigger data-testid="edit-dispatch-gold-karat"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="9kt">9kt</SelectItem>
                        <SelectItem value="14kt">14kt</SelectItem>
                        <SelectItem value="18kt">18kt</SelectItem>
                        <SelectItem value="22kt">22kt</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Diamond weight (ct)</Label>
                    <Input
                      type="number"
                      step="0.001"
                      value={form.diamond_weight}
                      onChange={e => setField('diamond_weight', e.target.value)}
                      data-testid="edit-dispatch-diamond-weight"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Metal value (₹)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.metal_value}
                      onChange={e => setField('metal_value', e.target.value)}
                      data-testid="edit-dispatch-metal-value"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Diamond & others value (₹)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.diamond_others_value}
                      onChange={e => setField('diamond_others_value', e.target.value)}
                      data-testid="edit-dispatch-diamond-value"
                    />
                  </div>
                </div>
              </div>

              <div className="pt-2 border-t">
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3">Bill-To override</p>
                <BillToPicker
                  open={billToOpen}
                  setOpen={setBillToOpen}
                  approvedCustomers={approvedCustomers}
                  loading={loadingCustomers}
                  orderCustomerName={null}
                  orderCustomerId={null}
                  value={{ id: form.bill_to_customer_id, name: form.bill_to_customer_name }}
                  onChange={(cust) => setForm(prev => ({ ...prev, bill_to_customer_id: cust?.id || '', bill_to_customer_name: cust?.company_name || '' }))}
                  testIdPrefix="edit-dispatch-billto"
                />
                {hasPayments && (
                  <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-2">
                    Bill-To cannot be changed while payments exist against this invoice.
                  </p>
                )}
              </div>

              <div className="pt-2 border-t">
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3">Payment terms</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Payment type</Label>
                    <Select value={form.payment_type || 'none'} onValueChange={v => setField('payment_type', v === 'none' ? '' : v)}>
                      <SelectTrigger data-testid="edit-dispatch-payment-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        <SelectItem value="cod">Cash on Delivery</SelectItem>
                        <SelectItem value="credit">Credit</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Metal credit days</Label>
                    <Input
                      type="number"
                      min="0"
                      value={form.metal_credit_days}
                      onChange={e => setField('metal_credit_days', e.target.value)}
                      data-testid="edit-dispatch-metal-credit-days"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Diamond credit days</Label>
                    <Input
                      type="number"
                      min="0"
                      value={form.diamond_credit_days}
                      onChange={e => setField('diamond_credit_days', e.target.value)}
                      data-testid="edit-dispatch-diamond-credit-days"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Metal due date</Label>
                    <Input
                      type="date"
                      value={form.metal_due_date || ''}
                      onChange={e => setField('metal_due_date', e.target.value)}
                      data-testid="edit-dispatch-metal-due-date"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Diamond due date</Label>
                    <Input
                      type="date"
                      value={form.diamond_due_date || ''}
                      onChange={e => setField('diamond_due_date', e.target.value)}
                      data-testid="edit-dispatch-diamond-due-date"
                    />
                  </div>
                </div>
              </div>

              <div className="pt-2 border-t">
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3">Metal exchange</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="flex items-center gap-2 pt-5">
                    <input
                      type="checkbox"
                      checked={!!form.has_metal_exchange}
                      onChange={e => setField('has_metal_exchange', e.target.checked)}
                      className="h-4 w-4"
                      data-testid="edit-dispatch-has-metal-exchange"
                    />
                    <Label className="text-xs">Has metal exchange</Label>
                  </div>
                  <div>
                    <Label className="text-xs">Exchange weight (g, 999)</Label>
                    <Input
                      type="number"
                      step="0.001"
                      value={form.metal_exchange_weight}
                      onChange={e => setField('metal_exchange_weight', e.target.value)}
                      disabled={!form.has_metal_exchange}
                      data-testid="edit-dispatch-metal-exchange-weight"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Exchange invoice #</Label>
                    <Input
                      value={form.metal_exchange_invoice_number}
                      onChange={e => setField('metal_exchange_invoice_number', e.target.value)}
                      disabled={!form.has_metal_exchange}
                      data-testid="edit-dispatch-metal-exchange-invoice"
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="edit-dispatch-save"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Pencil className="h-4 w-4 mr-1" />}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditDispatchDialog;
